import React, { useMemo } from 'react';
import { ChakraProvider, extendTheme } from '@chakra-ui/react';
import { Outlet, useParams } from 'react-router-dom';
import { withI18NextTranslate } from 'sp-ui';
import { LoadingIndicator } from 'ts/common/components';
import ClientPageHeading from 'ts/common/components/ClientPageHeading';
import ClientPageLayout from 'ts/common/components/ClientPageLayout';
import { getTheme } from 'ts/common/components/gallery';
import { useTranslate } from 'ts/common/hooks';
import { useBootstrapData, withBootstrapData } from 'client_react/bootstrap';
import { FetchMethod, useClientApiFetch } from 'client_react/bootstrap';

interface QuestionnaireError {
    detail?: string;
    status: number;
}

const isQuestionnaireError = (response: unknown): response is QuestionnaireError => {
    return (
        response !== null &&
        typeof response === 'object' &&
        'status' in response &&
        typeof (response as QuestionnaireError).status === 'number' &&
        (response as QuestionnaireError).status >= 400
    );
};

const QuestionnaireAppContent: React.FC = () => {
    const t = useTranslate('clientQuestionnaire');
    const { questionnaireId } = useParams<{ questionnaireId: string }>();

    const {
        response: questionnaire,
        loading,
        performFetch: refetchQuestionnaire
    } = useClientApiFetch<SpApi.Client.IQuestionnaire | QuestionnaireError>(
        `questionnaire/${questionnaireId}`,
        {
            method: FetchMethod.GET
        }
    );

    const theme = useMemo(
        () =>
            questionnaire && !isQuestionnaireError(questionnaire)
                ? extendTheme(getTheme(questionnaire.brandTheme))
                : extendTheme(),
        [questionnaire]
    );

    if (loading) {
        return (
            <ChakraProvider resetCSS={false} theme={theme}>
                <LoadingIndicator />
            </ChakraProvider>
        );
    }

    if (!questionnaire) {
        return (
            <div>
                <h1>{t('questionnaireNotFound')}</h1>
                <p>{t('questionnaireNotFoundMessage')}</p>
            </div>
        );
    }

    if (isQuestionnaireError(questionnaire)) {
        return (
            <div>
                <h1>{t('errorLoadingQuestionnaire')}</h1>
                <p>{t('unableToLoadQuestionnaire')}</p>
            </div>
        );
    }

    return (
        <ChakraProvider resetCSS={false} theme={theme}>
            <ClientPageLayout
                banner={<ClientPageHeading>{questionnaire.name}</ClientPageHeading>}
                headingText={questionnaire.brandName}
            >
                <Outlet context={{ questionnaire, refetchQuestionnaire }} />
            </ClientPageLayout>
        </ChakraProvider>
    );
};

const QuestionnaireApp: React.FC = withBootstrapData(() => {
    const { translations } = useBootstrapData();

    const PageWithTranslations = useMemo(
        () => withI18NextTranslate(QuestionnaireAppContent, translations),
        [translations]
    );

    return <PageWithTranslations />;
});

export default QuestionnaireApp;
